import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiGet } from '../../utils/api';
import { normalizeReviewImageUrl } from '../../utils/reviewImage';
import { safeReviewUrl } from '../../utils/reviewHtml';
import { usePageTitle } from '../../contexts/PageTitleContext';
import '../../css/Review/review.css';

const formatDate = (raw) => {
  if (!raw) return '';
  const normalized = String(raw).replace(' ', 'T');
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return String(raw).split(' ')[0] || String(raw);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}.${month}.${day}`;
};

const buildCruiseInfoText = (cruise = {}) => {
  const shipName = String(cruise?.name || '').trim();
  const dep = String(cruise?.departure_port_name || '').trim();
  const arr = String(cruise?.arrival_port_name || '').trim();
  const route = [dep, arr].filter(Boolean).join(' - ');

  if (shipName && route) return `${shipName} | ${route}`;
  if (route) return route;
  return shipName;
};

export default function ReviewList() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { setPageTitle } = usePageTitle();
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [meta, setMeta] = useState({ total: 0, page: 1, limit: 12 });

  const currentPage = useMemo(() => {
    const pageRaw = parseInt(searchParams.get('page') || '1', 10);
    return Number.isFinite(pageRaw) && pageRaw > 0 ? pageRaw : 1;
  }, [searchParams]);

  useEffect(() => {
    setPageTitle('고객 후기');
  }, [setPageTitle]);

  useEffect(() => {
    let mounted = true;
    setIsLoading(true);
    setError('');

    apiGet('/review/get_reviews.php', { page: currentPage, limit: 12 })
      .then((res) => {
        if (!mounted) return;
        if (res?.success) {
          const total = Math.max(0, Number(res.total || 0));
          const limit = Math.max(1, Number(res.limit || 12));
          const lastPage = Math.max(1, Math.ceil(total / limit));
          if (currentPage > lastPage) {
            setSearchParams(lastPage > 1 ? { page: String(lastPage) } : {}, { replace: true });
            return;
          }
          setItems(Array.isArray(res.data) ? res.data : []);
          setMeta({
            total: Number(res.total || 0),
            page: Number(res.page || currentPage),
            limit: Number(res.limit || 12),
          });
        } else {
          setItems([]);
          setError(res?.message || '리뷰 목록을 불러오지 못했습니다.');
        }
      })
      .catch(() => {
        if (!mounted) return;
        setItems([]);
        setError('리뷰 목록을 불러오지 못했습니다.');
      })
      .finally(() => {
        if (mounted) setIsLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [currentPage, setSearchParams]);

  const totalPages = Math.max(1, Math.ceil((meta.total || 0) / (meta.limit || 12)));
  const safePage = Math.min(currentPage, totalPages);

  const onPageChange = (nextPage) => {
    if (nextPage < 1 || nextPage > totalPages || nextPage === currentPage) return;
    const params = {};
    if (nextPage > 1) params.page = String(nextPage);
    setSearchParams(params);
  };

  return (
    <div className="review-page">
      <div className="review-container">
        <div className="review-header">
          <h2 className="review-title">와우크루즈 고객 후기</h2>
          <p className="review-subtitle">실제 예약 고객의 후기입니다.</p>
          <p className="review-subtitle">여행을 마친 예약의 마이페이지 예약상세에서 후기를 작성하실 수 있습니다.</p>
        </div>

        {isLoading && <div className="review-status">로딩 중...</div>}
        {!isLoading && error && <div className="review-status error">{error}</div>}
        {!isLoading && !error && items.length === 0 && (
          <div className="review-status">등록된 후기가 없습니다.</div>
        )}

        {!isLoading && !error && items.length > 0 && (
          <div className="review-grid">
            {items.map((item) => {
              const id = item?.id;
              const thumb = safeReviewUrl(normalizeReviewImageUrl(item?.thumbnail_url || ''), true);
              const cruise = item?.cruise || {};
              const cruiseInfoText = buildCruiseInfoText(cruise);
              return (
                <button
                  key={id}
                  type="button"
                  className="review-card"
                  onClick={() => navigate(`/review/${id}${safePage > 1 ? `?page=${safePage}` : ''}`)}
                >
                  <div className="review-card-thumb">
                    {thumb ? (
                      <img src={thumb} alt={item?.title || '후기 이미지'} />
                    ) : (
                      <div className="review-card-placeholder">NO IMAGE</div>
                    )}
                    <div className="review-card-overlay">
                      <div className="review-card-title">{item?.title || ''}</div>
                      {cruiseInfoText && <div className="review-card-ship">{cruiseInfoText}</div>}
                      <div className="review-card-link">자세히보기 {'>'}</div>
                      <div className="review-card-meta">
                        <span>{item?.author_name || '익명'}</span>
                        <span>{formatDate(item?.published_at)}</span>
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {!isLoading && !error && meta.total > meta.limit && (
          <div className="review-pagination">
            <button
              type="button"
              className="review-page-btn"
              disabled={safePage <= 1}
              onClick={() => onPageChange(safePage - 1)}
            >
              이전
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).slice(
              Math.max(0, safePage - 3),
              Math.max(0, safePage - 3) + 5
            ).map((page) => (
              <button
                key={page}
                type="button"
                className={`review-page-btn ${page === safePage ? 'active' : ''}`}
                onClick={() => onPageChange(page)}
              >
                {page}
              </button>
            ))}
            <button
              type="button"
              className="review-page-btn"
              disabled={safePage >= totalPages}
              onClick={() => onPageChange(safePage + 1)}
            >
              다음
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
